import { blinkConstants } from '@thoughtspot/blink-constants';
import { getEndEpochMS, getStartEpochMS, } from '@thoughtspot/custom-calendar-service';
import { isColorRotationDisabled as isColorRotationDisabledStyle } from '@thoughtspot/custom-style-service';
import * as dateUtil from '@thoughtspot/date-format-util';
import { getNextBucketEpoch } from '@thoughtspot/date-format-util/esm/date-util';
import { generateTooltipContentByAxisConfig as getContentByAxisConfig, getFormattedValueForNumericColumn, getTooltipSeriesInfo, getYAxisLabelForAttribute, } from '@thoughtspot/formatter-service';
import { getRandomInteger } from '@thoughtspot/js-util';
import { formatDataValue } from '@thoughtspot/number-format-util';
import { getId, getTimeBucket, isColumnValidForNumberFormatting, isDateColumn, isDateTimeColumn, isEffectivelyNumeric, } from '@thoughtspot/visualization-column-util';
import Highcharts from 'highcharts/es-modules/masters/highstock.src.js';
import _ from 'lodash';
import { CHART_CUSTOMISATION_CONFIG } from '../config/chart-advanced-customisation-config';
import { convertToAlpha, getDefaultChartsColorScale, } from '../core/chart-color-utils';
import { getChartType, getColorBySerieName, getColorScaleOfMultiColorSeriesColors, getIsYAxisColInLineStackedChart, getMultiColorSeriesColors, getSeriesColorsMap, getUpdatedSeriesColor, getXAxisColumns, getXAxisColumnsHash, isColorRotationDisabled, isDescendingSort, isTimeSeries, isXAxisOrdinalBased, } from '../core/chart-model-utils';
import { configOptions as chartConfigOptions, getEffectiveBarWidthForNonResponsiveLayout, isXAxisVertical, } from '../core/chart-type-specification-service';
import { isConditionSatisfied } from '../core/conditional-formatting-utils';
import { columnHasInfinityValues, getColumnConfig, getInfinityPlaceholder, getNegativeInfinityPlaceholder, getSeries, getSeriesColor, getXAxisLabelAt, } from '../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import { ChartThemeService } from '../theme/chart-theme-service';
import { ChartType, ColumnTimeBucket, } from '../types';
import { createLogger } from '../utils/logger';
const logger = createLogger('blink-highchart-config-util');
function getPrimaryColorCountForLegend(legendSize) {
    let variationColorCount;
    if (legendSize <= 8) {
        variationColorCount = 4;
    }
    else if (legendSize <= 15) {
        variationColorCount = 5;
    }
    else if (legendSize <= 24) {
        variationColorCount = 6;
    }
    else if (legendSize <= 35) {
        variationColorCount = 7;
    }
    else {
        variationColorCount = 8;
    }
    return variationColorCount;
}
function getColorHueIndexOrder(hueSize, isRainbow) {
    let sequence;
    switch (hueSize) {
        case 1:
            sequence = isRainbow ? [2] : [2];
            break;
        case 2:
            sequence = isRainbow ? [2, 0] : [2, 1];
            break;
        case 3:
            sequence = isRainbow ? [2, 0, 3] : [2, 1, 3];
            break;
        case 4:
            sequence = isRainbow ? [2, 0, 3, 1] : [2, 0, 3, 1];
            break;
        default:
            sequence = isRainbow ? [2, 0, 3, 1, 4] : [2, 0, 3, 1, 4];
            break;
    }
    return sequence;
}
function applyRainbowPattern(allColors, legendSize, primaryColorCount, colorVariationCount, usedColors) {
    const colors = [];
    const sameColorVariations = Math.ceil(legendSize / primaryColorCount);
    const hueIndexOrder = getColorHueIndexOrder(sameColorVariations, true);
    const uniqueUsedColors = _.uniq(usedColors);
    const maxUniqueRainbowColors = Math.min(hueIndexOrder.length * primaryColorCount, allColors.length);
    for (let hueIdx = 0; hueIdx < hueIndexOrder.length; ++hueIdx) {
        for (let primaryColorIdx = 0; primaryColorIdx < primaryColorCount; ++primaryColorIdx) {
            const colorIdx = primaryColorIdx * colorVariationCount + hueIndexOrder[hueIdx];
            const color = allColors[colorIdx];
            if (!uniqueUsedColors.includes(color.toUpperCase()) ||
                legendSize > maxUniqueRainbowColors) {
                colors.push(color);
            }
        }
    }
    return colors;
}
function applyDefaultPattern(allColors, legendSize, primaryColorCount, colorVariationCount, usedColors) {
    const colorMap = {};
    const colors = [];
    let counter = 0;
    const primaryColorsToUse = getPrimaryColorCountForLegend(legendSize);
    const sameColorVariations = Math.ceil(legendSize / primaryColorsToUse);
    const hueOrder = getColorHueIndexOrder(sameColorVariations, false);
    for (let variationIdx = 0; variationIdx < sameColorVariations; ++variationIdx) {
        for (let primaryColorIdx = 0; primaryColorIdx < primaryColorsToUse; ++primaryColorIdx) {
            counter += 1;
            if (counter <= legendSize) {
                colorMap[primaryColorIdx]
                    ? (colorMap[primaryColorIdx] += 1)
                    : (colorMap[primaryColorIdx] = 1);
            }
        }
    }
    Object.keys(colorMap).forEach(primeIdx => {
        let hueSize = colorMap[primeIdx];
        if (hueSize > hueOrder.length) {
            hueSize = hueOrder.length;
        }
        const idx = parseInt(primeIdx, 10);
        const baseColor = idx * colorVariationCount;
        const uniqueUsedColors = _.uniq(usedColors);
        for (let hueOrderIndex = 0; hueOrderIndex < hueSize; hueOrderIndex++) {
            const colorIdx = baseColor + hueOrder[hueOrderIndex];
            const color = allColors[colorIdx];
            if (!uniqueUsedColors.includes(color) ||
                legendSize > allColors.length) {
                colors.push(color);
            }
        }
    });
    return colors;
}
function getColors(allColors, legendSize, usedColors, disableColorRotation, useRainbowColors, disableRandomColor, useSystemDefaultColor) {
    const primaryColorCount = useRainbowColors
        ? blinkConstants.colorLayout.COLOR_COUNT_RAINBOW
        : blinkConstants.colorLayout.COLOR_COUNT_DEFAULT;
    const colorVariationCount = blinkConstants.colorLayout.COLOR_VARIATION_COUNT;
    let colors;
    if (legendSize > 1) {
        colors = useRainbowColors
            ? applyRainbowPattern(allColors, legendSize, primaryColorCount, colorVariationCount, usedColors)
            : applyDefaultPattern(allColors, legendSize, primaryColorCount, colorVariationCount, usedColors);
    }
    else if (useSystemDefaultColor) {
        colors = [getDefaultChartsColorScale()[1]];
    }
    else {
        const randomInt = disableColorRotation ||
            isColorRotationDisabledStyle() ||
            disableRandomColor
            ? 0
            : getRandomInteger(0, primaryColorCount - 2);
        colors = [allColors[randomInt * colorVariationCount + 2]];
    }
    colors = colors.slice(0, legendSize);
    return useRainbowColors ? colors : _.reverse(colors);
}
function updateSeriesWithMarkerSettings(series) {
    series.forEach(singleSeries => {
        if (singleSeries.data.length <= 1) {
            singleSeries.marker = {};
            singleSeries.marker.enabled = true;
        }
    });
}
const getDefaultQueryData = dataModel => {
    const dataArray = dataModel.data;
    const defaultQueryData = dataArray && dataArray[0];
    return defaultQueryData && defaultQueryData.getData();
};
export const updateMultiColorSeriesValuesInSeriesColorMap = (chartModel, dataModel, serie, seriesColors) => {
    var _a;
    const multiColorSeriesColors = getMultiColorSeriesColors(chartModel);
    if (_.isNil(multiColorSeriesColors) ||
        multiColorSeriesColors.length === 0) {
        return [];
    }
    const xAxisColumnHash = getXAxisColumnsHash(chartModel);
    const colorArray = getColorScaleOfMultiColorSeriesColors(multiColorSeriesColors, (_a = serie.blinkSeriesId) !== null && _a !== void 0 ? _a : serie.userOptions.blinkSeriesId, xAxisColumnHash);
    _.each(colorArray, (color, idx) => {
        if (_.isNil(color)) {
            return;
        }
        const label = getXAxisLabelAt(chartModel, dataModel, idx, undefined, undefined, getTranslatedTextCallback()) || '';
        const serieColor = _.trim(getSeriesColor(chartModel, dataModel, label));
        if (!_.isEmpty(label) && _.isEmpty(serieColor)) {
            seriesColors = getUpdatedSeriesColor(seriesColors, label, color);
        }
    });
    return seriesColors;
};
const getColorPalette = (chartModel, dataModel, disableRandomColor, getTranslatedText) => {
    const defaultQueryData = getDefaultQueryData(dataModel);
    const chartType = getChartType(chartModel);
    const configOptions = chartConfigOptions(chartType, chartModel);
    const series = getSeries(dataModel);
    const defaultTheme = ChartThemeService.getDefaultTheme();
    let allColors = defaultTheme.allColors;
    if (configOptions.useAlphaColors) {
        allColors = convertToAlpha(allColors);
    }
    const legendSize = configOptions.isMultiColorSeries
        ? !!defaultQueryData && defaultQueryData.length
        : series.length;
    const seriesColorMap = getSeriesColorsMap(chartModel);
    const usedColors = [];
    let isColorUpdatedNeeded = false;
    if (configOptions.isMultiColorSeries) {
        series.forEach(serie => {
            const updatedSeriesColor = updateMultiColorSeriesValuesInSeriesColorMap(chartModel, dataModel, serie, seriesColorMap);
            if (!_.isEmpty(updatedSeriesColor)) {
                const updatedClientState = {
                    ...chartModel.vizProp,
                    systemSeriesColors: updatedSeriesColor,
                    multiColorSeriesColors: [],
                    systemMultiColorSeriesColors: [],
                };
                _.set(chartModel, 'vizProp', updatedClientState);
                isColorUpdatedNeeded = true;
            }
            serie.data.forEach((point, index) => {
                const xAxisLabel = getXAxisLabelAt(chartModel, dataModel, index, null, null, getTranslatedText);
                const serieColor = getColorBySerieName(seriesColorMap, xAxisLabel);
                if (!_.isNil(serieColor)) {
                    usedColors.push(serieColor.toUpperCase());
                }
            });
        });
    }
    else {
        series.forEach(serie => {
            const isYAxisColInLineStackedChart = getIsYAxisColInLineStackedChart(chartModel, serie === null || serie === void 0 ? void 0 : serie.valueColumnIdentifier);
            const colorPicker = isYAxisColInLineStackedChart && serie.name
                ? serie.name
                : serie.blinkSeriesId;
            const blinkSerieColor = getColorBySerieName(seriesColorMap, colorPicker);
            if (!_.isNil(blinkSerieColor)) {
                usedColors.push(blinkSerieColor.toUpperCase());
            }
        });
    }
    const useSystemDefaultColor = chartType === ChartType.Heatmap || chartType === ChartType.Treemap;
    const colors = getColors(allColors, legendSize, usedColors, isColorRotationDisabled(chartModel), configOptions.useRainbowColors, disableRandomColor, useSystemDefaultColor);
    return {
        isColorUpdatedNeeded,
        colors,
    };
};
function getInfinityValue(column, chartModel, dataModel, value) {
    if (isEffectivelyNumeric(column)) {
        if (columnHasInfinityValues(dataModel, column)) {
            if (value >= getInfinityPlaceholder(dataModel, column)) {
                return '∞';
            }
            if (value <= getNegativeInfinityPlaceholder(dataModel, column)) {
                return '-∞';
            }
        }
    }
    return void 0;
}
const getDataLabelFromColumn = (chartModel, dataModel, labelColumn, value, getTranslatedText) => {
    if (isColumnValidForNumberFormatting(labelColumn)) {
        const infinityValue = getInfinityValue(labelColumn, chartModel, dataModel, value);
        if (infinityValue) {
            return infinityValue;
        }
        return getFormattedValueForNumericColumn(value, labelColumn);
    }
    return `${getYAxisLabelForAttribute(getColumnConfig(labelColumn, chartModel, dataModel), value, value)}`;
};
function getScrollEnabledMinMaxConfig(series, chartType, containerWidth, containerHeight, chartModel) {
    const highchartsOptions = Highcharts.getOptions();
    const axisLabelFontSize = highchartsOptions.xAxis.labels.style.fontSize !== 'inherit'
        ? highchartsOptions.xAxis.labels.style.fontSize
        : '12px';
    let sanitizedFontSize = axisLabelFontSize.match(/^(\d{1,2})px$/);
    sanitizedFontSize = +sanitizedFontSize[1];
    let minExtreme = null;
    let maxExtreme = null;
    let pointsOnScrollAxis = null;
    series.forEach(serie => {
        const data = serie.data;
        if (data.length > 0) {
            minExtreme =
                minExtreme !== null
                    ? Math.min(minExtreme, _.minBy(data, 'x').x)
                    : _.minBy(data, 'x').x;
            maxExtreme =
                maxExtreme != null
                    ? Math.max(maxExtreme, _.maxBy(data, 'x').x)
                    : _.maxBy(data, 'x').x;
        }
    });
    const effectiveBarWidth = getEffectiveBarWidthForNonResponsiveLayout(chartType, series, sanitizedFontSize);
    if (isXAxisVertical(chartType)) {
        pointsOnScrollAxis = Math.floor(containerHeight / effectiveBarWidth);
    }
    else {
        pointsOnScrollAxis = Math.floor(containerWidth / effectiveBarWidth);
    }
    let isReversed = false;
    const xAxisColumns = getXAxisColumns(chartModel);
    if (isTimeSeries(chartModel) && !isXAxisOrdinalBased(chartModel)) {
        isReversed = xAxisColumns.some(xAxisColumn => isDescendingSort(chartModel, xAxisColumn));
    }
    if (series.length) {
        const pointRange = _.head(series).pointRange || 1;
        if (isReversed) {
            minExtreme = Math.max(minExtreme, maxExtreme - pointsOnScrollAxis * pointRange);
        }
        else {
            maxExtreme = Math.min(maxExtreme, minExtreme + pointsOnScrollAxis * pointRange);
        }
    }
    return {
        min: minExtreme,
        max: maxExtreme,
    };
}
function hasSharedYAxisColumnsBetweenQueries(queries) {
    const yAxisColumnsinQueries = queries.map(query => {
        return query.yAxisColumns.map(col => getId(col));
    });
    for (let i = 0; i < yAxisColumnsinQueries.length; i++) {
        for (let j = i + 1; j < yAxisColumnsinQueries.length; j++) {
            if (_.intersection(yAxisColumnsinQueries[i], yAxisColumnsinQueries[j]).length > 0) {
                return true;
            }
        }
    }
    return false;
}
function getTitleText(samplingRatio, sampledMetrics, isDataSourceDefault, batchSize, getTranslatedText) {
    const { totalRowCount, totalRowsDisplayed } = sampledMetrics;
    let titleText;
    const formattedSampledMetricsInfo = {
        currentDataPointCount: formatDataValue(totalRowsDisplayed),
    };
    if (sampledMetrics.totalRowCount) {
        formattedSampledMetricsInfo.totalDataPointCount = formatDataValue(totalRowCount);
    }
    if (isDataSourceDefault) {
        if (samplingRatio < 1) {
            titleText = getTranslatedText('answer.chart.footer.infiniteMsg', formattedSampledMetricsInfo);
        }
        else {
            titleText = getTranslatedText('answer.chart.footer.finiteMsg', formattedSampledMetricsInfo);
        }
    }
    else {
        const hasMoreData = batchSize <= totalRowCount;
        if (hasMoreData) {
            titleText = getTranslatedText('answer.chart.footer.infiniteMsg', formattedSampledMetricsInfo);
        }
        else {
            titleText = getTranslatedText('answer.chart.footer.finiteMsg', formattedSampledMetricsInfo);
        }
    }
    return titleText;
}
function onRightClickPointSelect(options) {
    return options === null || options === void 0 ? void 0 : options.allowPointSelect;
}
function onClickedSelectedPoint(points, index) {
    return _.find(points, point => point.index === index);
}
function getIncompleteThresholdForCustomCalendar(dataModel, chartModel, dateNow, seriesIndex) {
    const series = getSeries(dataModel);
    const xAxisColumns = getXAxisColumns(chartModel);
    const timeBucket = getTimeBucket(xAxisColumns[0]);
    const customCalendarMap = dataModel.getDisplayCustomCalendarMapForColumn(xAxisColumns[0]);
    const seriesData = series[seriesIndex].data;
    let incompleteThresholdIndex = seriesData.length - 1;
    seriesData.forEach((point, idx) => {
        const xVal = point.xRawValues[0];
        const calendarPoint = customCalendarMap[xVal];
        if (_.isNil(calendarPoint)) {
            logger.warn(`Calender Point is null for x value: ${xVal}`);
            return;
        }
        let endDate = null;
        if (!_.isNil(getEndEpochMS(calendarPoint))) {
            endDate = getEndEpochMS(calendarPoint);
        }
        else {
            const startDate = getStartEpochMS(calendarPoint);
            endDate = getNextBucketEpoch(startDate, timeBucket);
        }
        if (endDate > dateNow &&
            incompleteThresholdIndex > 0 &&
            idx > 0 &&
            incompleteThresholdIndex > idx - 1) {
            incompleteThresholdIndex = idx - 1;
        }
    });
    return seriesData[incompleteThresholdIndex].x;
}
function validateCfCondition(conditionalMetric, allSeries, serieIndex, axis, dataRow, inScopeParameters) {
    let lhsColumnValue;
    let rhsColumnValue;
    const tooltipDataContent = dataRow.tooltipDataContent;
    if (conditionalMetric.lhsColumnId) {
        const dataPoint = tooltipDataContent === null || tooltipDataContent === void 0 ? void 0 : tooltipDataContent[conditionalMetric.lhsColumnId];
        lhsColumnValue = dataPoint === null || dataPoint === void 0 ? void 0 : dataPoint.dataValue;
    }
    if (conditionalMetric.rhsColumnId) {
        const dataPoint = tooltipDataContent === null || tooltipDataContent === void 0 ? void 0 : tooltipDataContent[conditionalMetric.rhsColumnId];
        rhsColumnValue = dataPoint === null || dataPoint === void 0 ? void 0 : dataPoint.dataValue;
    }
    return isConditionSatisfied(conditionalMetric, dataRow[axis], inScopeParameters, lhsColumnValue, rhsColumnValue);
}
const getScreenReaderData = (pointLabelObject) => {
    const props = pointLabelObject.series.chart.userOptions.chart.props;
    const seriesInfo = getTooltipSeriesInfo.call(pointLabelObject);
    return getContentByAxisConfig(seriesInfo, props)
        .map(pointData => `${pointData.label} ${pointData.value}`)
        .join('. ');
};
const chartLevelAdvancedSettings = (chartType, chartSettings) => {
    const supportedKeysForChart = [];
    const otherSettingsSection = CHART_CUSTOMISATION_CONFIG.find(configElement => configElement.key === 'answerLevelSettings');
    if (otherSettingsSection && otherSettingsSection.children) {
        otherSettingsSection.children.forEach(childElement => {
            if (!childElement.chartTypesNotSupported ||
                !childElement.chartTypesNotSupported.includes(chartType)) {
                supportedKeysForChart.push(childElement.key);
            }
        });
    }
    Object.entries(chartSettings).forEach(([key, value]) => {
        if (supportedKeysForChart.includes(key)) {
            chartSettings[key] = value;
        }
        else {
            chartSettings[key] = null;
        }
    });
    return chartSettings;
};
const shouldShowDataLabels = (series, visibleSerieIds, visibility) => {
    if (!visibility)
        return false;
    const threshold = getEnvFlags().maxLabelCount;
    const visiblePointsCount = series.reduce((accumulator, serie) => {
        var _a, _b;
        if (visibleSerieIds.includes(serie.userOptions.blinkSeriesId)) {
            return (_b = accumulator + ((_a = serie.points) === null || _a === void 0 ? void 0 : _a.length)) !== null && _b !== void 0 ? _b : 0;
        }
        return accumulator;
    }, 0);
    if (visiblePointsCount > threshold) {
        logger.warn('Data labels are disabled for performance reasons. Number of points exceeds the threshold.', { visiblePointsCount, threshold });
        return false;
    }
    return true;
};
const getMinTickIntervalForXAxis = (chartModel) => {
    let minTickInterval = null;
    const xAxisColumn = getXAxisColumns(chartModel)[0];
    if (!_.isEmpty(xAxisColumn.formatPattern)) {
        minTickInterval = dateUtil.getIntervalMillisForFormatPattern(xAxisColumn.formatPattern);
    }
    else {
        minTickInterval = dateUtil.getIntervalMillisForTimeBucket(getTimeBucket(xAxisColumn));
        const isNoTimeBucketColumn = xAxisColumn.timeBucket === ColumnTimeBucket.NoBucket;
        if (isDateColumn(xAxisColumn) &&
            !isDateTimeColumn(xAxisColumn) &&
            isNoTimeBucketColumn) {
            minTickInterval = dateUtil.getIntervalMillisForTimeBucket(dateUtil.timeBuckets[ColumnTimeBucket.Daily]);
        }
    }
    return minTickInterval;
};
export { getColors, updateSeriesWithMarkerSettings, getScrollEnabledMinMaxConfig, getDataLabelFromColumn, getColorPalette, hasSharedYAxisColumnsBetweenQueries, getIncompleteThresholdForCustomCalendar, getTitleText, onRightClickPointSelect, onClickedSelectedPoint, validateCfCondition, getScreenReaderData, chartLevelAdvancedSettings, shouldShowDataLabels, getMinTickIntervalForXAxis, };
//# sourceMappingURL=blink-highchart-config-util.js.map